import { useEffect } from 'react';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

export default function WomenChildrenWar() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <div className="relative h-[70vh] min-h-[600px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9d9acf67c10f55bb339ccd078b963729.jpg"
            alt="Women and children in conflict zones"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <div className="inline-block px-4 py-2 bg-red-600 rounded-full text-sm font-medium mb-6">
            Humanitarian Crisis
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            The Invisible Victims: Women and Children in War
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8">
            Understanding the unique horrors faced by the most vulnerable in conflict zones
          </p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <span>15 min read</span>
            <span>•</span>
            <span>Emergency Resources</span>
            <span>•</span>
            <span>Updated January 2025</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        
        {/* Personal Note */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-l-4 border-purple-600 rounded-lg p-8 mb-12">
          <div className="flex items-start gap-4">
            <i className="ri-heart-fill text-3xl text-purple-600 flex-shrink-0"></i>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Why I'm Writing This</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Every day, I see the news. Gaza. Ukraine. Sudan. Syria. Afghanistan. And every time, I think about the women and children—the ones whose stories don't make headlines, whose suffering is just a statistic.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                But they're not statistics. They're mothers trying to keep their babies alive. They're teenage girls hiding from soldiers. They're children who've forgotten what peace sounds like.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This article is for them. To make their suffering visible. To explain what they face. And to remind the world that we cannot look away. 💜
              </p>
            </div>
          </div>
        </div>

        {/* The Reality Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Brutal Reality</h2>
          
          <div className="bg-red-50 border-l-4 border-red-600 rounded-lg p-6 mb-8">
            <p className="text-lg text-gray-800 font-semibold mb-2">
              In every war zone around the world right now:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>Women and children make up <strong>80% of all refugees and displaced people</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>Sexual violence is used as a <strong>weapon of war</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>Children are <strong>recruited as soldiers</strong>, some as young as 8 years old</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>Pregnant women give birth <strong>in bomb shelters without medical care</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>Girls are <strong>forced into early marriage</strong> as families try to protect them</span>
              </li>
            </ul>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            War doesn't just kill. It destroys everything that makes life worth living. And for women and children, the horrors are unique, systematic, and often invisible to the outside world.
          </p>
        </section>

        {/* What Women Face */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What Women Face in War Zones</h2>

          {/* Sexual Violence */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-fill text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Sexual Violence as a Weapon</h3>
                <p className="text-sm text-red-600 font-semibold">Trigger Warning: Sexual Violence</p>
              </div>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                In conflict zones, rape is not random. It's <strong>systematic</strong>. It's used to terrorize communities, humiliate men, and destroy the social fabric of entire populations.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="font-semibold mb-3">The Reality:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">→</span>
                    <span><strong>Gang rape</strong> in front of family members</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">→</span>
                    <span><strong>Sexual slavery</strong> and forced prostitution</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">→</span>
                    <span><strong>Forced pregnancy</strong> to "dilute" ethnic groups</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">→</span>
                    <span><strong>Genital mutilation</strong> and torture</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">→</span>
                    <span><strong>HIV/AIDS</strong> deliberately spread as a weapon</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed">
                <strong>In Gaza:</strong> Women report being strip-searched by male soldiers, sexually assaulted during raids, and living in constant fear of violence.
              </p>
              
              <p className="leading-relaxed">
                <strong>In Ukraine:</strong> Documented cases of Russian soldiers raping women and girls as young as 4 years old, often killing them afterward.
              </p>
              
              <p className="leading-relaxed">
                <strong>In Sudan:</strong> The Rapid Support Forces use rape as ethnic cleansing, with mass sexual violence against Darfuri women.
              </p>

              <p className="leading-relaxed italic text-gray-600">
                And after? Many survivors face rejection from their communities, forced to carry the trauma alone.
              </p>
            </div>
          </div>

          {/* Maternal Health Crisis */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-heart-pulse-fill text-2xl text-pink-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Maternal Health Crisis</h3>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                Imagine being pregnant in a war zone. No hospitals. No doctors. No clean water. Just bombs and fear.
              </p>
              
              <div className="bg-pink-50 rounded-lg p-6">
                <p className="font-semibold mb-3">Pregnant women in war zones face:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-pink-600">•</span>
                    <span><strong>No prenatal care</strong> - No ultrasounds, no check-ups, no vitamins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-pink-600">•</span>
                    <span><strong>Giving birth in bomb shelters</strong> - On concrete floors, in the dark</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-pink-600">•</span>
                    <span><strong>No pain relief</strong> - No epidurals, no medication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-pink-600">•</span>
                    <span><strong>Complications = death</strong> - C-sections impossible, bleeding unstoppable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-pink-600">•</span>
                    <span><strong>Malnutrition</strong> - Not enough food for themselves or their babies</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed">
                <strong>In Gaza:</strong> Over 50,000 pregnant women with no access to healthcare. Women having C-sections without anesthesia. Babies born premature because of stress and malnutrition.
              </p>
              
              <p className="leading-relaxed">
                <strong>In Afghanistan:</strong> Maternal mortality rates skyrocketed after the Taliban banned female healthcare workers, leaving women with no one to help them give birth.
              </p>

              <p className="leading-relaxed font-semibold text-pink-700">
                Every day, women die giving birth in war zones—deaths that would be completely preventable in peacetime.
              </p>
            </div>
          </div>

          {/* Displacement & Survival */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-home-heart-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Displacement & Daily Survival</h3>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                When war comes, women become the primary caregivers for entire families—often alone, because the men are fighting, dead, or disappeared.
              </p>
              
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="font-semibold mb-3">A day in the life of a displaced woman:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">5 AM:</span>
                    <span>Wake up in a tent or bombed-out building. Check if children are still breathing.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">6 AM:</span>
                    <span>Walk 2 hours to get water (if there's any). Risk of assault on the way.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">9 AM:</span>
                    <span>Try to find food. Maybe there's aid distribution. Maybe not.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">12 PM:</span>
                    <span>Cook whatever you found over a fire. One meal for 6 people.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">3 PM:</span>
                    <span>Try to wash clothes in dirty water. No soap. No privacy.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">6 PM:</span>
                    <span>Comfort crying children. Tell them it will be okay. You don't believe it.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">9 PM:</span>
                    <span>Try to sleep. Listen for bombs. Pray you wake up tomorrow.</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed">
                <strong>No menstrual products.</strong> Women use rags, newspaper, or nothing. They bleed through their clothes and feel ashamed.
              </p>
              
              <p className="leading-relaxed">
                <strong>No privacy.</strong> Shared toilets (if any). Bathing in public. Changing clothes in front of strangers.
              </p>
              
              <p className="leading-relaxed">
                <strong>No safety.</strong> Refugee camps are dangerous. Women are assaulted going to the bathroom at night. They stop drinking water so they don't have to go.
              </p>
            </div>
          </div>

          {/* Economic Exploitation */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-money-dollar-circle-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Economic Exploitation & Survival Sex</h3>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                When you have no money, no job, and children to feed, what do you do?
              </p>
              
              <p className="leading-relaxed">
                Many women are forced into "<strong>survival sex</strong>"—trading their bodies for food, shelter, or safe passage. It's not a choice. It's survival.
              </p>

              <div className="bg-yellow-50 rounded-lg p-6">
                <p className="font-semibold mb-3">The exploitation cycle:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">→</span>
                    <span>Sex for food from aid workers or soldiers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">→</span>
                    <span>Forced into prostitution by traffickers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">→</span>
                    <span>Early marriage of daughters to reduce family burden</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">→</span>
                    <span>Exploitation by landlords demanding sexual favors for shelter</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed italic text-gray-600">
                These women aren't "prostitutes." They're mothers doing whatever it takes to keep their children alive.
              </p>
            </div>
          </div>
        </section>

        {/* What Children Face */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What Children Face in War Zones</h2>

          {/* Physical Violence */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-cross-fill text-2xl text-red-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Death, Injury, and Trauma</h3>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                Children are not spared in modern warfare. They are bombed, shot, and maimed just like adults—but they don't understand why.
              </p>
              
              <div className="bg-red-50 rounded-lg p-6">
                <p className="font-semibold mb-3">The statistics are horrifying:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">•</span>
                    <span><strong>In Gaza:</strong> Over 14,000 children killed in just 3 months (2023-2024)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">•</span>
                    <span><strong>In Ukraine:</strong> Hundreds of children killed, thousands injured</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">•</span>
                    <span><strong>In Yemen:</strong> 11,000+ children killed or injured since 2015</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">•</span>
                    <span><strong>In Syria:</strong> 13,000+ children killed in 12 years of war</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed">
                Children lose limbs. They're paralyzed. They're burned. They watch their parents die. They dig through rubble looking for their siblings.
              </p>

              <p className="leading-relaxed font-semibold text-red-700">
                And the ones who survive? They carry trauma that will last a lifetime.
              </p>
            </div>
          </div>

          {/* Psychological Trauma */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-brain-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Psychological Trauma</h3>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                Imagine being 5 years old and knowing what a bomb sounds like. Imagine being 8 and having nightmares every single night. Imagine being 12 and not remembering what peace feels like.
              </p>
              
              <div className="bg-purple-50 rounded-lg p-6">
                <p className="font-semibold mb-3">Children in war zones experience:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    <span><strong>PTSD</strong> - Flashbacks, nightmares, panic attacks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    <span><strong>Anxiety and depression</strong> - Constant fear, hopelessness</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    <span><strong>Developmental delays</strong> - Trauma affects brain development</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    <span><strong>Behavioral problems</strong> - Aggression, withdrawal, regression</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    <span><strong>Loss of childhood</strong> - No play, no joy, no innocence</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed">
                <strong>In Gaza:</strong> Children draw pictures of bombs and dead bodies. They play "funeral" instead of "house." They don't cry anymore—they're numb.
              </p>
              
              <p className="leading-relaxed">
                <strong>In Ukraine:</strong> Children sleep in subway stations, flinching at every sound. They ask their parents, "Will we die today?"
              </p>

              <p className="leading-relaxed italic text-gray-600">
                These children will carry this trauma into adulthood. Many will never fully recover.
              </p>
            </div>
          </div>

          {/* Education Loss */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-book-open-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Loss of Education</h3>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                Schools are bombed. Teachers are killed. Books are burned. Millions of children lose years of education—and with it, their future.
              </p>
              
              <div className="bg-blue-50 rounded-lg p-6">
                <p className="font-semibold mb-3">The education crisis:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span><strong>In Gaza:</strong> All schools closed, 625,000 children out of school</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span><strong>In Afghanistan:</strong> 1.4 million girls banned from school by Taliban</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span><strong>In Syria:</strong> 2.4 million children out of school</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span><strong>In Yemen:</strong> 2 million children not in school</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed">
                <strong>Girls suffer most.</strong> When families can only afford to send one child to school, they choose boys. When girls are kept home to help with chores, fetch water, or care for siblings.
              </p>

              <p className="leading-relaxed font-semibold text-blue-700">
                A generation of children is growing up illiterate, with no skills, no opportunities, and no hope.
              </p>
            </div>
          </div>

          {/* Child Soldiers */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-sword-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Child Soldiers</h3>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                In many conflicts, children are kidnapped and forced to become soldiers. Some are as young as 8 years old.
              </p>
              
              <div className="bg-gray-100 rounded-lg p-6">
                <p className="font-semibold mb-3">What happens to child soldiers:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-gray-700">→</span>
                    <span><strong>Forced to kill</strong> - Including their own family members</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-700">→</span>
                    <span><strong>Drugged</strong> - To make them fearless and obedient</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-700">→</span>
                    <span><strong>Sexually abused</strong> - Especially girls, used as "wives" for commanders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-700">→</span>
                    <span><strong>Brainwashed</strong> - Taught to hate, to kill without mercy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-700">→</span>
                    <span><strong>Abandoned</strong> - If they escape or are rescued, communities reject them</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed">
                <strong>Worldwide:</strong> An estimated 250,000 children are currently serving as child soldiers in conflicts around the world.
              </p>

              <p className="leading-relaxed italic text-gray-600">
                These children have their innocence stolen. They become both victims and perpetrators, carrying guilt and trauma that may never heal.
              </p>
            </div>
          </div>

          {/* Malnutrition & Disease */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-heart-pulse-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Malnutrition & Disease</h3>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                War destroys food systems, water supplies, and healthcare. Children starve. They die from preventable diseases.
              </p>
              
              <div className="bg-green-50 rounded-lg p-6">
                <p className="font-semibold mb-3">The health crisis:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">•</span>
                    <span><strong>In Gaza:</strong> 90% of children under 2 face severe food poverty</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">•</span>
                    <span><strong>In Yemen:</strong> 2.2 million children acutely malnourished</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">•</span>
                    <span><strong>In Sudan:</strong> 730,000 children at risk of death from malnutrition</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">•</span>
                    <span><strong>In Afghanistan:</strong> 3.2 million children need treatment for acute malnutrition</span>
                  </li>
                </ul>
              </div>

              <p className="leading-relaxed">
                Children develop stunted growth that can never be reversed. Their brains don't develop properly. They're more vulnerable to disease.
              </p>

              <p className="leading-relaxed">
                <strong>Preventable diseases kill thousands:</strong> Cholera, measles, pneumonia, diarrhea—diseases that would be easily treated in peacetime become death sentences.
              </p>

              <p className="leading-relaxed font-semibold text-green-700">
                More children die from hunger and disease in war than from bombs and bullets.
              </p>
            </div>
          </div>
        </section>

        {/* Current Crises */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Current Crises: Where It's Happening Now</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Gaza */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🇵🇸 Gaza, Palestine</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>14,000+ children killed (as of Jan 2024)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>1.7 million people displaced</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>All hospitals barely functioning</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>Famine conditions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>No safe zones</span>
                </li>
              </ul>
            </div>

            {/* Ukraine */}
            <div className="bg-gradient-to-br from-blue-50 to-yellow-50 border border-blue-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🇺🇦 Ukraine</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Hundreds of children killed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>6.3 million internally displaced</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Widespread sexual violence</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>19,000+ children deported to Russia</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Ongoing attacks on civilians</span>
                </li>
              </ul>
            </div>

            {/* Sudan */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🇸🇩 Sudan</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">•</span>
                  <span>Ethnic cleansing in Darfur</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">•</span>
                  <span>Mass rape as weapon of war</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">•</span>
                  <span>730,000 children at risk of starvation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">•</span>
                  <span>7.1 million displaced</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">•</span>
                  <span>World's largest displacement crisis</span>
                </li>
              </ul>
            </div>

            {/* Afghanistan */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🇦🇫 Afghanistan</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600">•</span>
                  <span>1.4 million girls banned from school</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600">•</span>
                  <span>Women banned from most work</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600">•</span>
                  <span>3.2 million children malnourished</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600">•</span>
                  <span>Forced child marriages increasing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600">•</span>
                  <span>Gender apartheid</span>
                </li>
              </ul>
            </div>

            {/* Syria */}
            <div className="bg-gradient-to-br from-red-50 to-pink-50 border border-red-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🇸🇾 Syria</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>13,000+ children killed in 12 years</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>6.9 million internally displaced</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>2.4 million children out of school</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>Widespread use of chemical weapons</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">•</span>
                  <span>Ongoing humanitarian crisis</span>
                </li>
              </ul>
            </div>

            {/* Yemen */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🇾🇪 Yemen</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600">•</span>
                  <span>11,000+ children killed or injured</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600">•</span>
                  <span>2.2 million children malnourished</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600">•</span>
                  <span>2 million children out of school</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600">•</span>
                  <span>World's worst humanitarian crisis</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600">•</span>
                  <span>Cholera and disease outbreaks</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* What Can We Do */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What Can We Do?</h2>

          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border-l-4 border-teal-600 rounded-lg p-8 mb-8">
            <p className="text-lg text-gray-800 leading-relaxed mb-4">
              It's easy to feel helpless. The suffering is so immense, so overwhelming. But we cannot look away. We cannot become numb to their pain.
            </p>
            <p className="text-lg text-gray-800 leading-relaxed font-semibold">
              Every action matters. Every voice matters. Every person who refuses to be silent matters.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Donate */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-hand-heart-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Donate</h3>
              <p className="text-gray-700 mb-4">
                Support organizations providing emergency aid, medical care, and shelter to women and children in conflict zones.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• UNICEF</li>
                <li>• Doctors Without Borders</li>
                <li>• Save the Children</li>
                <li>• Women for Women International</li>
                <li>• UNRWA (for Gaza)</li>
              </ul>
            </div>

            {/* Advocate */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-megaphone-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Advocate</h3>
              <p className="text-gray-700 mb-4">
                Use your voice to demand action from governments and international organizations.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Sign petitions</li>
                <li>• Contact your representatives</li>
                <li>• Attend protests and rallies</li>
                <li>• Support ceasefire campaigns</li>
                <li>• Demand humanitarian aid access</li>
              </ul>
            </div>

            {/* Educate */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Educate</h3>
              <p className="text-gray-700 mb-4">
                Share information, amplify voices from conflict zones, and combat misinformation.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Share articles and reports</li>
                <li>• Follow journalists on the ground</li>
                <li>• Amplify survivor testimonies</li>
                <li>• Educate your community</li>
                <li>• Challenge propaganda</li>
              </ul>
            </div>

            {/* Support Refugees */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-home-heart-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Support Refugees</h3>
              <p className="text-gray-700 mb-4">
                Help refugees and displaced people in your own community rebuild their lives.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Volunteer with refugee organizations</li>
                <li>• Donate clothes, furniture, supplies</li>
                <li>• Offer language tutoring</li>
                <li>• Help with job searches</li>
                <li>• Be a welcoming neighbor</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Final Message */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-purple-100 via-pink-100 to-red-100 rounded-2xl p-10 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              They Are Not Statistics
            </h2>
            <p className="text-lg text-gray-800 leading-relaxed mb-6 max-w-3xl mx-auto">
              Every number in this article is a person. A mother. A daughter. A child with dreams. They deserve to be seen. They deserve to be heard. They deserve to live in peace.
            </p>
            <p className="text-xl font-bold text-purple-700 mb-8">
              We cannot save everyone. But we can refuse to look away.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="px-8 py-4 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Support
              </a>
              <a
                href="/demands"
                className="px-8 py-4 bg-purple-600 text-white rounded-full font-semibold hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Sign Petitions
              </a>
              <a
                href="/volunteer"
                className="px-8 py-4 bg-pink-600 text-white rounded-full font-semibold hover:bg-pink-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Volunteer
              </a>
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Resources & Organizations</h2>
          
          <div className="bg-gray-50 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Emergency Aid Organizations:</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>UNICEF:</strong> unicef.org</li>
                  <li>• <strong>Save the Children:</strong> savethechildren.org</li>
                  <li>• <strong>Doctors Without Borders:</strong> msf.org</li>
                  <li>• <strong>UNRWA:</strong> unrwa.org</li>
                  <li>• <strong>International Rescue Committee:</strong> rescue.org</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Women's Organizations:</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>Women for Women International:</strong> womenforwomen.org</li>
                  <li>• <strong>UN Women:</strong> unwomen.org</li>
                  <li>• <strong>Global Fund for Women:</strong> globalfundforwomen.org</li>
                  <li>• <strong>Women's Refugee Commission:</strong> womensrefugeecommission.org</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Human Rights Organizations:</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>Amnesty International:</strong> amnesty.org</li>
                  <li>• <strong>Human Rights Watch:</strong> hrw.org</li>
                  <li>• <strong>International Committee of the Red Cross:</strong> icrc.org</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Child Protection:</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>War Child:</strong> warchild.org</li>
                  <li>• <strong>Child Soldiers International:</strong> child-soldiers.org</li>
                  <li>• <strong>Plan International:</strong> plan-international.org</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Share Section */}
        <div className="border-t border-gray-200 pt-8">
          <p className="text-center text-gray-600 mb-4">Share this article to raise awareness</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                const text = "The Invisible Victims: Women and Children in War - Understanding what they face in conflict zones";
                const url = window.location.href;
                window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
              }}
              className="px-6 py-3 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-twitter-x-line mr-2"></i>
              Share on X
            </button>
            <button
              onClick={() => {
                const url = window.location.href;
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-facebook-fill mr-2"></i>
              Share on Facebook
            </button>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}